import { NETWORK_DIFF_URL_BASE } from '@/constants'

/**
 * The set of URL segments that, when they appear directly after the
 * `/<networkId>/` prefix, signal a network sub-route (history, diagnosis,
 * assumption sets, etc.) rather than the bare digraph.
 *
 * Lives in its own module so AppShell's `isDigraphRoute` and Sidebar's
 * `matchDigraph` consume the SAME source of truth. Adding a new
 * `/<networkId>/<feature>` route now means appending one entry here.
 */
export const NETWORK_SUB_ROUTES: ReadonlyArray<string> = [
  'history',
  'commits',
  'diagnosis',
  'assumption-set',
  'pivoted-assumption-set',
  NETWORK_DIFF_URL_BASE,
  'assumption-set-diff',
  'variable-types',
]

/** Returns true when `pathname` is the digraph of `networkId` — i.e.
 * `/<networkId>` or `/<networkId>/<viewId>`, but not one of the
 * network sub-routes above. */
export const matchDigraph = (pathname: string, networkId: string): boolean => {
  const segments = pathname.split('/').filter(Boolean)
  if (segments[0] !== networkId) return false
  if (segments.length === 1) return true
  return !NETWORK_SUB_ROUTES.includes(segments[1] ?? '')
}

export const shouldUseStickyViewFallback = (
  pathname: string,
  urlNetworkId: string | undefined,
): boolean => {
  if (!urlNetworkId) return true

  const segments = pathname.split('/').filter(Boolean)
  return !(segments.length === 1 && segments[0] === urlNetworkId)
}

export type StickyNetworkReconcile = {
  networkId: string | undefined
  clearView: boolean
}

/**
 * Single source of truth for reconciling AppShell's sticky network selection
 * (`kbe.lastNetworkId`). Returns the write to apply — the next stored network
 * id and whether to also drop the sticky view — or `null` when the store is
 * already correct.
 *
 * Ownership is explicit and one-directional, which is what makes the reconcile
 * loop-free. When a network id is in the URL, the URL owns the selection: it is
 * persisted and NEVER cleared here (the route already validated it). Only when
 * there is no URL network do we drop a sticky id that no longer exists in the
 * fetched list.
 *
 * Previously this logic lived in two separate effects — one persisting the URL
 * id, one clearing a stored id absent from the list — that both wrote and
 * observed the same store key. When the URL id was present but missing from the
 * list (e.g. deleting the currently-open network, whose list refetch drops the
 * id before navigation leaves `/<id>`), they ping-ponged forever: clear →
 * re-persist → clear → "Maximum update depth exceeded" (React #185, KBE-690).
 * Collapsing them into this single owner removes the disagreement by
 * construction. A genuinely deleted URL network is handled by the page's
 * `useNetwork` NOT_FOUND redirect; once the URL leaves it the stale sticky id
 * is cleared normally.
 *
 * The function is a pure state transition and MUST reach a fixed point:
 * applying its own output to the store and calling again returns `null`. That
 * property is the guarantee the effect cannot loop, and is asserted in tests.
 */
export const resolveStickyNetworkReconcile = (
  storedNetworkId: string | undefined,
  urlNetworkId: string | undefined,
  networksList: ReadonlyArray<{ id: string }> | undefined,
): StickyNetworkReconcile | null => {
  if (urlNetworkId) {
    if (urlNetworkId === storedNetworkId) return null
    return { networkId: urlNetworkId, clearView: false }
  }
  if (
    storedNetworkId &&
    networksList &&
    !networksList.some((n) => n.id === storedNetworkId)
  ) {
    return { networkId: undefined, clearView: true }
  }
  return null
}

/**
 * Top-level routes that are NOT scoped to a single network in the URL.
 * On these, switching networks in the header selector should refresh the
 * sticky selection in place rather than navigate away (there is no
 * network segment to swap). The network-browser routes (`/` and `/list`)
 * are deliberately absent: selecting a network there means "open it".
 */
const NON_NETWORK_ROUTE_ROOTS: ReadonlyArray<string> = [
  'assistant',
  'optimization-jobs',
  'performance',
  'admin',
]

/**
 * Destination for switching the selected network from the header selector,
 * preserving the page the user is on instead of always jumping to the
 * digraph.
 *
 * - `/` (home browser) → `/<newNetworkId>` (open its digraph)
 * - `/list/...` → `/list/<newNetworkId>` (stay in the list browser)
 * - `/<oldId>/<subRoute>` → `/<newNetworkId>/<subRoute>` (keep history,
 *   diagnosis, assumption-set, … the user is viewing)
 * - `/<oldId>` or `/<oldId>/<viewId>` → `/<newNetworkId>` (digraph; the
 *   view id is dropped because it belonged to the previous network)
 * - global pages (`/assistant`, `/optimization-jobs`, …) → `null`,
 *   signalling the caller to update the sticky selection without
 *   navigating.
 */
export const buildNetworkSwitchUrl = (
  pathname: string,
  newNetworkId: string,
): string | null => {
  const segments = pathname.split('/').filter(Boolean)
  if (segments.length === 0) return `/${newNetworkId}`

  const top = segments[0]
  if (top === 'list') return `/list/${newNetworkId}`
  if (NON_NETWORK_ROUTE_ROOTS.includes(top)) return null

  // `top` is the current network id. Keep a sub-route if present; drop a
  // bare digraph view id.
  const sub = segments[1]
  if (sub && NETWORK_SUB_ROUTES.includes(sub)) {
    return `/${newNetworkId}/${sub}`
  }
  return `/${newNetworkId}`
}

/**
 * Network sub-routes that carry the selected view in the URL as a
 * `?viewId=` query (the sidebar links append it). On these the page
 * either uses the view directly or preserves it as context, so switching
 * views must update the URL — not just sticky state — to keep the URL,
 * the selector, and the page content in sync. Sub-routes absent here
 * (network-diff, assumption-set-diff) have no view slot.
 */
const VIEW_QUERY_SUB_ROUTES: ReadonlyArray<string> = [
  'history',
  'diagnosis',
  'assumption-set',
  'pivoted-assumption-set',
  'variable-types',
]

/**
 * Destination for switching the selected view from the header selector.
 * Returns a URL to navigate to when the current route carries the view
 * (so the page actually refreshes with the new view), or `null` when the
 * route has no place for a view — in which case the caller updates the
 * sticky view selection in place without navigating.
 *
 * - digraph (`/<net>` or `/<net>/<viewId>`) → `/<net>/<viewId>`
 * - list (`/list/...`) → `/list/<net>/<viewId>`
 * - home (`/`) → `/<net>/<viewId>` (open the digraph at that view)
 * - view-aware sub-routes → `/<net>/<sub>?viewId=<viewId>`
 * - everything else (network-diff, assumption-set-diff, /assistant,
 *   /optimization-jobs, /performance, /admin/*) → `null`
 */
export const buildViewSwitchUrl = (
  pathname: string,
  networkId: string,
  viewId: string,
): string | null => {
  const segments = pathname.split('/').filter(Boolean)
  if (segments.length === 0) return `/${networkId}/${viewId}`

  const top = segments[0]
  if (top === 'list') return `/list/${networkId}/${viewId}`
  if (NON_NETWORK_ROUTE_ROOTS.includes(top)) return null

  // `top` is the current network id.
  const sub = segments[1]
  if (!sub || !NETWORK_SUB_ROUTES.includes(sub)) {
    // bare digraph or `/<net>/<viewId>` digraph
    return `/${networkId}/${viewId}`
  }
  if (VIEW_QUERY_SUB_ROUTES.includes(sub)) {
    return `/${networkId}/${sub}?viewId=${viewId}`
  }
  return null
}
